/**
 * ericSugerenciasService — fábrica de servicio para el catálogo de sugerencias de
 * preguntas de ERIC. El catálogo es fuente de verdad del microservicio vec-fleet-eric
 * (GET /api/eric/sugerencias, proxeado por el monolito); acá solo se consume y se
 * cachea en localStorage con TTL de 1 día para no pegarle a la API en cada visita.
 *
 * El micro devuelve solo ids i18n + el texto canónico en español:
 *   { data: { sugerencias: [ { id: 'eric.sugerencia.vehiculos_en_taller',
 *                              texto: '¿Cuántos vehículos tengo en taller?' }, ... ] } }
 * El front traduce cada id vía react-intl (lang files) usando `texto` como
 * defaultMessage de fallback.
 *
 * Uso:
 *   var svc = crearEricSugerenciasService(this.ajaxHandler);
 *   svc.cargarCatalogo().then(function(catalogo) { ... });  // [{ id, texto }]
 *
 * docker exec -it vecfleet-web-workspace-1 bash -c "cd /var/www && CI=true npm test -- --testPathPattern='ericSugerenciasService'"
 */

var CACHE_KEY = 'ericSugerenciasCache';

// Clave de día local (YYYY-MM-DD) para invalidar el cache una vez por jornada.
function _hoy() {
  var d = new Date();
  var mes = ('0' + (d.getMonth() + 1)).slice(-2);
  var dia = ('0' + d.getDate()).slice(-2);
  return d.getFullYear() + '-' + mes + '-' + dia;
}

// Lee el catálogo cacheado si es del día de hoy; null si no hay, está vencido o corrupto.
function _leerCache() {
  try {
    var raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || parsed.fecha !== _hoy() || !Array.isArray(parsed.sugerencias)) {
      return null;
    }
    return parsed.sugerencias;
  } catch (e) {
    return null;
  }
}

function _guardarCache(sugerencias) {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ fecha: _hoy(), sugerencias: sugerencias })
    );
  } catch (e) {
    // Cuota llena / modo privado: el cache es best-effort, seguimos sin él.
  }
}

// Normaliza la lista del backend descartando items sin id o sin texto.
function _normalizar(lista) {
  if (!Array.isArray(lista)) return [];
  return lista
    .filter(function (s) { return s && s.id && s.texto; })
    .map(function (s) { return { id: s.id, texto: s.texto }; });
}

/**
 * Crea el servicio de sugerencias.
 *
 * @param {object} ajaxHandler - instancia de AjaxHandler ya suscripta en el componente
 * @returns {{ cargarCatalogo: Function }}
 */
function crearEricSugerenciasService(ajaxHandler) {
  /**
   * Devuelve el catálogo completo de sugerencias [{ id, texto }].
   *
   * Si hay cache vigente del día, lo resuelve sin llamar a la API. Si no, pega al
   * backend, cachea el resultado y lo devuelve. Ante error de red o respuesta no-200
   * resuelve con [] (la pantalla de inicio simplemente no muestra chips).
   *
   * @returns {Promise<Array<{ id: string, texto: string }>>}
   */
  function cargarCatalogo() {
    var cache = _leerCache();
    if (cache) {
      return Promise.resolve(cache);
    }
    return ajaxHandler
      .fetch('/eric/sugerencias', {
        method: 'GET',
        silent: true,
      })
      .then(function (response) {
        if (!response || response.status !== 200) {
          return [];
        }
        return response.json().then(function (data) {
          var payload = data && data.data ? data.data : {};
          var normalizada = _normalizar(payload.sugerencias);
          if (normalizada.length > 0) {
            _guardarCache(normalizada);
          }
          return normalizada;
        });
      })
      .catch(function () {
        return [];
      });
  }

  return {
    cargarCatalogo: cargarCatalogo,
  };
}

export { _leerCache, _guardarCache, _normalizar, CACHE_KEY };
export default crearEricSugerenciasService;
